import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./SHProducts.module.scss";
import { Modal } from "../../../secondary";
import { useWoundInfinitePagination } from "../../../../hooks/useWoundInfinitePagination";
import { useDebounce } from "../../../../utils";
import { useLoading } from "../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../constants/loadingKeys";
import {
  getSkinHealthProducts,
  resetSkinHealthProducts,
  getProductMasterByIdAsync,
  getProductMasterMaterialByIdAsync,
  getDivisionDescription,
  sendProductNotFoundEmailAsync,
} from "../../../../app/homeSlice";
import searchIcon from "../../../../static/images/search-icon.svg";
import { ProductCard } from "./ProductCard";
import { ProductDetailView } from "./ProductDetailView";
import { MaterialDetailView } from "./MaterialDetailView";
import { ProductDetailSkeleton } from "./ProductDetailSkeleton";
import { ProductCardSkeletonList } from "./ProductCardSkeletonList";
import { NoProducts } from "./NoProducts";
import { useRequestMoreInfo } from "../hooks/useRequestMoreInfo";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 50;

export const SHProducts = ({ modalOpen, modalClose, initialSearchTerm = "" }) => {
  const dispatch = useDispatch();

  const products = useSelector((s) => s.home.skinHealthProducts);
  const metadata = useSelector((s) => s.home.skinHealthMetadata);
  const divisionDescription = useSelector((s) => s.home.divisionDescription);

  const [division, setDivision] = useState("");
  const [searchInput, setSearchInput] = useState(initialSearchTerm);
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  const hasMore = Boolean(metadata?.next);

  const { loading, loadMoreRef, resetPagination } = useWoundInfinitePagination({
    hasMore,
    limit: PAGE_SIZE,
    onFetch: (offset) => dispatch(getSkinHealthProducts({ search: trimmedSearch, division, offset, limit: PAGE_SIZE })),
  });

  const [view, setView] = useState("list");

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const detailLoading = useLoading(LOADING_KEYS.GET_PRODUCT_MASTER_BY_ID);

  const [materialDetail, setMaterialDetail] = useState(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const materialLoading = useLoading(LOADING_KEYS.GET_PRODUCT_MASTER_MATERIAL_BY_ID);
  const isNoProductEmailLoading = useLoading(LOADING_KEYS.PRODUCT_NOT_FOUND_EMAIL);

  const { moreInfoLoading, reqSuccessMessage, setReqSuccessMessage, handleRequestMoreInfo } = useRequestMoreInfo({
    selectedProductId,
    selectedMaterialId,
  });

  const latestDetailRequestRef = useRef(0);

  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setView("list");
    setProductDetail(null);
    setMaterialDetail(null);
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedSearch]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    dispatch(getDivisionDescription());
  }, [dispatch]);

  const handleSearch = (e) => {
    setSearchInput(e?.target?.value);
    setDivision("");
  };

  const handleProductClick = async (productFamilyId) => {
    setView("detail");
    setSelectedProductId(productFamilyId);
    setProductDetail(null);
    setMaterialDetail(null);
    setReqSuccessMessage(null);

    const requestId = ++latestDetailRequestRef.current;
    const data = await dispatch(getProductMasterByIdAsync(productFamilyId));

    if (latestDetailRequestRef.current !== requestId) return;

    if (data) {
      setProductDetail(data);
    }
  };

  const handleMaterialClick = async (materialId) => {
    setView("material");
    setMaterialDetail(null);
    setSelectedMaterialId(materialId);
    setReqSuccessMessage(null);

    const requestId = ++latestDetailRequestRef.current;
    const data = await dispatch(getProductMasterMaterialByIdAsync(materialId));

    if (latestDetailRequestRef.current !== requestId) return;

    if (data) {
      setMaterialDetail(data);
    }
  };

  const handleBackToResults = () => {
    setView("list");
    setProductDetail(null);
    setMaterialDetail(null);
    setReqSuccessMessage(null);
  };

  const handleBackToProduct = () => {
    setView("detail");
    setMaterialDetail(null);
    setReqSuccessMessage(null);
  };

  const handleNoProductEmail = async () => {
    const payload = { appVersion: "4.0.61", searchText: trimmedSearch, pageName: "Productsearch" };

    const response = await dispatch(sendProductNotFoundEmailAsync(payload));
    setSuccessMessage(response);
  };

  const handleDivisionCode = (key) => {
    setDivision(key);
    setSearchInput("");
  };

  const handleClose = () => {
    dispatch(resetSkinHealthProducts());
    setView("list");
    setProductDetail(null);
    setMaterialDetail(null);
    modalClose();
  };

  const isInitialLoading = loading && products.length === 0;
  const isPaginationLoading = loading && products.length > 0;

  const showEmptyState = !isInitialLoading && products.length === 0 && trimmedSearch.length >= MIN_SEARCH_LENGTH;

  return (
    <Modal modalOpen={modalOpen} modalClose={handleClose} heading="Skin Health Products" modalSize={styles.custom_modal_size} custom_heading="pt-0">
      <div className={styles.sh_products}>
        <div className={styles.search_bar_wrapper}>
          <img className={styles.icon} src={searchIcon} alt="search icon" />
          <input type="text" placeholder="Find skin health products" value={searchInput} onChange={handleSearch} autoFocus />
          {searchInput && (
            <button type="button" className={styles.clear_btn} onClick={() => setSearchInput("")} aria-label="Clear search">
              &times;
            </button>
          )}
        </div>

        {view === "list" && !isInitialLoading && products.length > 0 && (
          <p className={styles.result_hint}>Here are the most relevant products matching your search term</p>
        )}

        <div className={styles.results_container}>
          {view === "list" && (
            <>
              {isInitialLoading && <ProductCardSkeletonList count={6} />}

              {showEmptyState && (
                <NoProducts
                  search={trimmedSearch}
                  divisions={divisionDescription}
                  successMessage={successMessage}
                  loading={isNoProductEmailLoading}
                  onEmail={handleNoProductEmail}
                  onBrowse={handleDivisionCode}
                />
              )}

              {products.length > 0 && (
                <>
                  <div className={styles.card_list}>
                    {products.map((product) => (
                      <ProductCard key={product.productFamilyId} product={product} onClick={handleProductClick} />
                    ))}
                  </div>

                  <div ref={loadMoreRef} className={styles.sentinel} />

                  {isPaginationLoading && (
                    <div className={styles.pagination_loader}>
                      <span className={styles.spinner} />
                      <span>Loading more products…</span>
                    </div>
                  )}

                  {!hasMore && !isPaginationLoading && <p className={styles.end_of_results}>No more products.</p>}
                </>
              )}
            </>
          )}

          {view === "detail" && (
            <>
              {detailLoading && !productDetail && <ProductDetailSkeleton />}

              {productDetail && (
                <ProductDetailView
                  product={productDetail}
                  onBack={handleBackToResults}
                  onMaterialClick={(materialId) => handleMaterialClick(materialId)}
                  onRequestMoreInfo={handleRequestMoreInfo}
                  successMessage={reqSuccessMessage}
                  loading={moreInfoLoading}
                />
              )}
            </>
          )}

          {view === "material" && (
            <>
              {materialLoading && !materialDetail && <ProductDetailSkeleton />}

              {materialDetail && (
                <MaterialDetailView
                  material={materialDetail}
                  onBack={handleBackToProduct}
                  onRequestMoreInfo={handleRequestMoreInfo}
                  successMessage={reqSuccessMessage}
                  loading={moreInfoLoading}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
